/**
 * ConfirmDialog — the one way to ask before something irreversible.
 *
 * `Alert.alert` is not an option: react-native-web ships it as an empty no-op,
 * so on app.tago.rent a confirm built on it silently does nothing and the
 * destructive path either runs unguarded or dies. This is a real Modal, which
 * behaves the same on all three platforms.
 *
 * Extracted from the vehicle-delete modal that lived inline in
 * hosting/listings.tsx, rather than copied into the second caller — the listing
 * wizard's close button, which until now destroyed an entire draft on one tap
 * with nothing in between.
 *
 * `destructive` paints the confirm action in the error colour and is the
 * default, because that is what this component is for.
 */
import { useState } from 'react'
import { Text, View } from 'react-native'
import { AppModal } from './AppModal'
import { Button } from './Button'
import { GlassSheet } from './GlassSheet'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, SPACING, TEXT_STYLES } from '../../lib/theme/tokens'
import { FONTS } from '../../lib/brand'

interface ConfirmDialogProps {
  visible: boolean
  title: string
  /** What actually happens. Say the consequence, not "are you sure".  */
  message?: string
  confirmLabel: string
  cancelLabel: string
  onConfirm: () => void
  onCancel: () => void
  /**
   * Back gesture / Esc / backdrop. MUST be non-destructive.
   *
   * Defaults to `onCancel` for the many dialogs where cancel is the safe close.
   * But when `onCancel` is itself a destructive action (e.g. "empezar de cero",
   * which deletes a draft), a caller MUST pass an explicit dismiss so an Android
   * back press cannot trigger irreversible loss — the reflexive gesture must be
   * the safe one.
   */
  onDismiss?: () => void
  /** Icon shown above the title — pass the error-coloured one for deletions. */
  icon?: React.ReactNode
  destructive?: boolean
  confirmLoading?: boolean
  /**
   * Explicit testIDs. Callers migrated off an inline modal keep their existing
   * ids so the Maestro flows that target them do not have to be rewritten —
   * validate-maestro-flows.mjs holds those selectors to the app's testID
   * contract and will reject a silent rename.
   */
  testIDs?: { overlay?: string; cancel?: string; confirm?: string }
  testIDPrefix?: string
}

/**
 * Two equal half-width buttons only work for very short labels. Spanish action
 * copy such as "Seguí tu borrador" cannot fit inside the dialog's 340dp card:
 * its intrinsic width pushes over the sibling and turns the CTA into a clipped
 * pill. Stack before that happens. Short pairs ("Sí" / "No") keep the compact
 * row; descriptive actions get one full-width line each on every platform,
 * with the secondary choice rendered as a quiet text action.
 */
function shouldStackActions(confirmLabel: string, cancelLabel: string): boolean {
  const confirmLength = confirmLabel.trim().length
  const cancelLength = cancelLabel.trim().length
  // Keep a comfortable minimum hit target for both actions. Two descriptive
  // Spanish labels in a 340pt card otherwise squeeze the text into a pill or
  // let it paint outside the button (the resume-draft modal regression).
  return Math.max(confirmLength, cancelLength) > 12 || confirmLength + cancelLength > 20
}

export function ConfirmDialog({
  visible,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  onDismiss,
  icon,
  destructive = true,
  confirmLoading = false,
  testIDs,
  testIDPrefix = 'confirm',
}: ConfirmDialogProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const stackActions = shouldStackActions(confirmLabel, cancelLabel)

  const confirmAction = (
    <Button
      title={confirmLabel}
      variant="primary"
      loading={confirmLoading}
      onPress={onConfirm}
      testID={testIDs?.confirm ?? `${testIDPrefix}.confirm`}
      accessibilityLabel={confirmLabel}
      style={destructive ? { backgroundColor: colors.error } : undefined}
    />
  )
  const cancelAction = (
    <Button
      title={cancelLabel}
      variant={stackActions ? 'text' : 'secondary'}
      onPress={onCancel}
      testID={testIDs?.cancel ?? `${testIDPrefix}.cancel`}
      accessibilityLabel={cancelLabel}
    />
  )

  return (
    <AppModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss ?? onCancel}
    >
      <View
        testID={testIDs?.overlay ?? `${testIDPrefix}.overlay`}
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: SPACING.lg,
        }}
      >
        <GlassSheet
          style={{
            width: '100%',
            maxWidth: 360,
          }}
          contentStyle={{
            padding: SPACING.xl,
            alignItems: 'center',
            gap: SPACING.sm,
          }}
        >
          {icon}
          <Text
            style={{
              fontSize: TEXT_STYLES.h4.fontSize,
              lineHeight: TEXT_STYLES.h4.lineHeight,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
              textAlign: 'center',
            }}
          >
            {title}
          </Text>
          {message ? (
            <Text
              style={{
                fontSize: TEXT_STYLES.bodySm.fontSize,
                lineHeight: TEXT_STYLES.bodySm.lineHeight,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                textAlign: 'center',
              }}
            >
              {message}
            </Text>
          ) : null}

          <View
            style={{
              alignSelf: 'stretch',
              flexDirection: stackActions ? 'column' : 'row',
              gap: SPACING.sm,
              marginTop: SPACING.sm,
            }}
          >
            {stackActions ? (
              <>
                <View style={{ width: '100%' }}>{confirmAction}</View>
                <View style={{ width: '100%' }}>{cancelAction}</View>
              </>
            ) : (
              <>
                <View style={{ flex: 1, minWidth: 0 }}>{cancelAction}</View>
                <View style={{ flex: 1, minWidth: 0 }}>{confirmAction}</View>
              </>
            )}
          </View>
        </GlassSheet>
      </View>
    </AppModal>
  )
}

/** Small helper so callers do not each re-invent the open/close state. */
export function useConfirmDialog() {
  const [visible, setVisible] = useState(false)
  return { visible, open: () => setVisible(true), close: () => setVisible(false) }
}
